import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { canViewAdminSettings } from '../../permissions';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Combines the two previously-separate admin cards (AI Features toggle +
// Claude API key management + Test Connection) into one page. They're
// coupled anyway -- flipping AI Features on without a valid key just
// yields errors -- so putting them together reduces confusion.
export default function AIConfigPage() {
  const { user, getHeaders } = useAuth();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/settings`, { headers: getHeaders() });
      if (res.ok) setSettings(await res.json());
    } catch (err) { /* keep previous state on error */ }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => {
    if (canViewAdminSettings(user)) fetchSettings();
    else setLoading(false);
  }, [user, fetchSettings]);

  const claudeKeySetting = settings.find(s => s.key === 'claude_api_key');
  const claudeEnabled = settings.find(s => s.key === 'claude_ai_enabled')?.value === 'true';

  async function toggleAi() {
    const newVal = claudeEnabled ? 'false' : 'true';
    try {
      await fetch(`${API_BASE}/settings/claude_ai_enabled`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ value: newVal, isSecret: false }),
      });
      await fetchSettings();
    } catch (err) { alert('Error toggling AI: ' + err.message); }
  }

  async function handleSaveKey() {
    if (!inputValue.trim()) return;
    setSaving(true);
    setSavedMessage('');
    try {
      const res = await fetch(`${API_BASE}/settings/claude_api_key`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ value: inputValue, isSecret: true }),
      });
      if (res.ok) {
        setSavedMessage('Saved');
        setEditing(false);
        setInputValue('');
        await fetchSettings();
        setTimeout(() => setSavedMessage(''), 3000);
      } else {
        const data = await res.json();
        setSavedMessage(data.error || 'Failed to save');
      }
    } catch (err) { setSavedMessage('Network error'); }
    setSaving(false);
  }

  async function testConnection() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_BASE}/settings/test-claude`, {
        method: 'POST', headers: getHeaders(),
      });
      setTestResult(await res.json());
    } catch (err) { setTestResult({ success: false, error: 'Network error' }); }
    setTesting(false);
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }

  if (!canViewAdminSettings(user)) {
    return (
      <div className="page-content">
        <div className="empty-state"><h3>Admin only</h3><p>AI configuration is admin-only.</p></div>
      </div>
    );
  }

  return (
    <>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Link to="/settings" className="btn btn-secondary btn-sm" style={{ textDecoration: 'none' }}>&larr; Settings</Link>
          <h2>AI Configuration</h2>
        </div>
      </div>

      <div className="page-content" style={{ maxWidth: '760px' }}>
        {loading && <div style={{ padding: '20px', color: '#777' }}>Loading...</div>}

        {/* AI Features toggle */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="card-header"><h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>AI Features</h3></div>
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
              <div style={{ flex: 1, paddingRight: '16px' }}>
                <div style={{ fontSize: '14px', fontWeight: 500 }}>Claude AI &mdash; Bid Extraction</div>
                <div style={{ fontSize: '12px', color: '#777', lineHeight: 1.4, marginTop: '2px' }}>
                  When enabled, uploaded bid documents are automatically analyzed by Claude AI to extract sub name, amounts, and line items. Applies platform-wide. Requires a valid API key below.
                </div>
              </div>
              <button
                className={`btn ${claudeEnabled ? 'btn-primary' : 'btn-secondary'}`}
                style={{ minWidth: '70px' }}
                onClick={toggleAi}
                disabled={loading}
              >
                {claudeEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
            {claudeEnabled && !claudeKeySetting && (
              <div style={{
                marginTop: '12px', padding: '8px 12px', borderRadius: '6px',
                background: '#fef3c7', border: '1px solid #fcd34d', color: '#78350f',
                fontSize: '12px', lineHeight: 1.4,
              }}>
                AI is ON but no API key is set below. Uploads will fail until a key is added.
              </div>
            )}
          </div>
        </div>

        {/* API Key */}
        <div className="card">
          <div className="card-header"><h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>API Keys</h3></div>
          <div className="card-body">
            <div className="form-group">
              <label style={{ fontWeight: 600, marginBottom: '6px', display: 'block' }}>Claude API Key</label>
              {!editing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{
                    fontFamily: 'monospace', fontSize: '14px',
                    color: claudeKeySetting ? '#333' : '#999',
                    background: '#f5f5f5', padding: '8px 12px', borderRadius: '6px', flex: 1,
                  }}>
                    {claudeKeySetting ? claudeKeySetting.value : 'Not set'}
                  </span>
                  <button className="btn btn-secondary" onClick={() => setEditing(true)} style={{ whiteSpace: 'nowrap' }}>
                    {claudeKeySetting ? 'Edit' : 'Add Key'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="password" className="form-control"
                    value={inputValue} onChange={e => setInputValue(e.target.value)}
                    placeholder="sk-ant-..."
                    style={{ flex: 1, fontFamily: 'monospace', fontSize: '14px' }}
                    autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveKey(); }}
                  />
                  <button className="btn btn-primary" onClick={handleSaveKey} disabled={saving || !inputValue.trim()}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button className="btn btn-secondary" onClick={() => { setEditing(false); setInputValue(''); setSavedMessage(''); }}>
                    Cancel
                  </button>
                </div>
              )}

              {savedMessage && (
                <div style={{ marginTop: '8px', color: savedMessage === 'Saved' ? '#16a34a' : '#dc2626', fontSize: '13px', fontWeight: 500 }}>
                  {savedMessage}
                </div>
              )}

              {claudeKeySetting && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#999' }}>
                  {claudeKeySetting.updatedBy && <span>Updated by {claudeKeySetting.updatedBy}</span>}
                  {claudeKeySetting.updatedAt && <span>{claudeKeySetting.updatedBy ? ' on ' : 'Updated '}{formatDate(claudeKeySetting.updatedAt)}</span>}
                </div>
              )}

              {claudeKeySetting && !editing && (
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e5e5' }}>
                  <button className="btn btn-accent" onClick={testConnection} disabled={testing}>
                    {testing ? 'Testing...' : 'Test Connection'}
                  </button>
                  {testResult && (
                    <div style={{
                      marginTop: '12px', padding: '12px 16px', borderRadius: '8px',
                      background: testResult.success ? '#dcfce7' : '#fee2e2',
                      border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                    }}>
                      <div style={{
                        fontWeight: 600, fontSize: '13px',
                        color: testResult.success ? '#166534' : '#991b1b',
                        marginBottom: '4px',
                      }}>
                        {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                      </div>
                      {testResult.success ? (
                        <>
                          <div style={{ fontSize: '13px', color: '#166534' }}>{testResult.response}</div>
                          <div style={{ fontSize: '11px', color: '#15803d', marginTop: '4px' }}>
                            Model: {testResult.model} &middot; Tokens: {testResult.usage?.input_tokens} in / {testResult.usage?.output_tokens} out
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#991b1b' }}>{testResult.error}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
